import { useState, useEffect, useCallback } from 'react';
import {
  getReuniones,
  getReunionesForMonth,
  getReunionesPaginated,
  createReunion,
  updateReunion,
  deleteReunion,
  type Reunion,
  type ReunionWithCounts,
  type ReunionInsert,
  type ReunionUpdate,
  type ReunionFilters
} from '../lib/api/reuniones';
import type { PaginationInfo, PaginatedResponse } from '../lib/api/pagination';
import { DEFAULT_PAGE_SIZE } from '../lib/api/pagination';
import { logger } from '../lib/logger';
import { queryCache, generateCacheKey, CacheTTL } from '../lib/cache/queryCache';
import { useOrganization } from '../contexts/OrganizationContext';

export function useReuniones(filters?: ReunionFilters, refreshTrigger?: number) {
  const [reuniones, setReuniones] = useState<ReunionWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { currentOrg } = useOrganization();

  const fetchReuniones = useCallback(async (isBackgroundRefresh = false) => {
    try {
      // Only show loading spinner on initial load, not on background refresh
      if (!isBackgroundRefresh) {
        setLoading(true);
      }

      // Generate cache key based on organization and filters
      const cacheKey = generateCacheKey('reuniones', currentOrg?.id, filters);

      // Check cache first
      const cachedData = queryCache.get<ReunionWithCounts[]>(cacheKey);
      if (cachedData) {
        setReuniones(cachedData);
        setError(null);
        setLoading(false);
        return;
      }

      // Cache miss - fetch from API
      const data = await getReuniones(filters);

      // Store in cache (1 minute TTL)
      queryCache.set(cacheKey, data, CacheTTL.MEDIUM);

      setReuniones(data);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [filters?.municipalityId, filters?.status, filters?.dateFrom, filters?.dateTo, filters?.searchText, currentOrg?.id]);

  // Initial fetch and filter changes
  useEffect(() => {
    fetchReuniones(false);
  }, [fetchReuniones]);

  // Background refresh when refreshTrigger changes (after initial mount)
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      // Invalidate cache before background refresh to ensure fresh data
      // This is critical: without invalidation, fetchReuniones returns stale cached data
      if (currentOrg?.id) {
        queryCache.invalidate(`reuniones|org:${currentOrg.id}`);
      }
      // This is a background refresh - don't show loading spinner
      fetchReuniones(true);
    }
  }, [refreshTrigger, currentOrg?.id]); // Added currentOrg.id dependency for cache invalidation

  const addReunion = async (reunion: ReunionInsert) => {
    const newReunion = await createReunion(reunion);

    // Invalidate all reuniones cache for this organization
    if (currentOrg?.id) {
      queryCache.invalidate(`reuniones|org:${currentOrg.id}`);
    }

    // Add default counts for new reunion (they start at 0)
    const reunionWithCounts: ReunionWithCounts = { ...newReunion, propuestasCount: 0, gestionesCount: 0 };
    setReuniones(prev => [reunionWithCounts, ...prev]);
    return newReunion;
  };

  const modifyReunion = async (id: string, updates: ReunionUpdate) => {
    const updated = await updateReunion(id, updates);

    // Invalidate all reuniones cache for this organization
    if (currentOrg?.id) {
      queryCache.invalidate(`reuniones|org:${currentOrg.id}`);
    }

    // Preserve existing counts when updating
    setReuniones(prev => prev.map(r => r.id === id ? { ...updated, propuestasCount: r.propuestasCount, gestionesCount: r.gestionesCount } : r));
    return updated;
  };

  const removeReunion = async (id: string) => {
    await deleteReunion(id);

    // Invalidate all reuniones cache for this organization
    if (currentOrg?.id) {
      queryCache.invalidate(`reuniones|org:${currentOrg.id}`);
    }

    setReuniones(prev => prev.filter(r => r.id !== id));
  };

  return {
    reuniones,
    loading,
    error,
    refresh: fetchReuniones,
    addReunion,
    modifyReunion,
    removeReunion
  };
}

export function useReunionesForCalendar(year: number, month: number) {
  const [reuniones, setReuniones] = useState<ReunionWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { currentOrg } = useOrganization();

  const refresh = useCallback(() => {
    // Invalidate cache for this month when refresh is called
    if (currentOrg?.id) {
      const cacheKey = generateCacheKey('reuniones-calendar', currentOrg.id, { year, month });
      queryCache.invalidateKey(cacheKey);
    }
    setRefreshTrigger(prev => prev + 1);
  }, [currentOrg?.id, year, month]);

  useEffect(() => {
    let isMounted = true;

    async function fetchData() {
      try {
        logger.log('[useReunionesForCalendar] Starting fetch for', year, month);
        setLoading(true);

        // Generate cache key based on organization, year, and month
        const cacheKey = generateCacheKey('reuniones-calendar', currentOrg?.id, { year, month });

        // Check cache first
        const cachedData = queryCache.get<ReunionWithCounts[]>(cacheKey);
        if (cachedData && isMounted) {
          logger.log('[useReunionesForCalendar] Cache hit for', year, month);
          setReuniones(cachedData);
          setError(null);
          setLoading(false);
          return;
        }

        // Cache miss - fetch from API
        const data = await getReunionesForMonth(year, month);

        // Store in cache (1 minute TTL)
        queryCache.set(cacheKey, data, CacheTTL.MEDIUM);

        logger.log('[useReunionesForCalendar] Fetched', data.length, 'reuniones with counts:', data.map(r => ({ id: r.id, title: r.title, date: r.date, time: r.time, propuestas: r.propuestasCount, gestiones: r.gestionesCount })));
        if (isMounted) {
          setReuniones(data);
          setError(null);
        }
      } catch (err) {
        if (isMounted) {
          setError(err as Error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [year, month, refreshTrigger, currentOrg?.id]);

  return { reuniones, loading, error, refresh };
}

/**
 * Paginated version of useReuniones hook
 * SCALE-CRIT-001 Fix: Supports pagination for scalable data fetching
 * SCALE-HIGH-001 Fix: Adds caching layer to prevent redundant API calls
 */
export function useReunionesPaginated(filters?: ReunionFilters) {
  const [reuniones, setReuniones] = useState<ReunionWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const { currentOrg } = useOrganization();

  const fetchReuniones = useCallback(async (pageNum?: number) => {
    try {
      setLoading(true);
      const targetPage = pageNum ?? page;

      // Generate cache key including page number
      const cacheKey = generateCacheKey('reuniones-paginated', currentOrg?.id, {
        ...filters,
        page: targetPage,
      });

      // Check cache first
      const cachedData = queryCache.get<PaginatedResponse<ReunionWithCounts>>(cacheKey);
      if (cachedData) {
        setReuniones(cachedData.data);
        setPagination(cachedData.pagination);
        setError(null);
        setLoading(false);
        return;
      }

      // Cache miss - fetch from API
      const response = await getReunionesPaginated(filters, { page: targetPage });

      // Store in cache (1 minute TTL)
      queryCache.set(cacheKey, response, CacheTTL.MEDIUM);

      setReuniones(response.data);
      setPagination(response.pagination);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [filters?.municipalityId, filters?.status, filters?.dateFrom, filters?.dateTo, filters?.searchText, page, currentOrg?.id]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [filters?.municipalityId, filters?.status, filters?.dateFrom, filters?.dateTo, filters?.searchText]);

  // Fetch when page or filters change
  useEffect(() => {
    fetchReuniones(page);
  }, [page, filters?.municipalityId, filters?.status, filters?.dateFrom, filters?.dateTo, filters?.searchText]);

  const goToPage = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPage(newPage);
    }
  };

  const addReunion = async (reunion: ReunionInsert) => {
    const newReunion = await createReunion(reunion);

    // Invalidate all reuniones caches for this organization
    if (currentOrg?.id) {
      queryCache.invalidate(`reuniones|org:${currentOrg.id}`);
      queryCache.invalidate(`reuniones-paginated|org:${currentOrg.id}`);
      queryCache.invalidate(`reuniones-calendar|org:${currentOrg.id}`);
    }

    // Refresh current page to show updated data
    await fetchReuniones(page);
    return newReunion;
  };

  const modifyReunion = async (id: string, updates: ReunionUpdate) => {
    const updated = await updateReunion(id, updates);

    // Invalidate all reuniones caches for this organization
    if (currentOrg?.id) {
      queryCache.invalidate(`reuniones|org:${currentOrg.id}`);
      queryCache.invalidate(`reuniones-paginated|org:${currentOrg.id}`);
      queryCache.invalidate(`reuniones-calendar|org:${currentOrg.id}`);
    }

    setReuniones(prev => prev.map(r => r.id === id ? { ...r, ...updated } : r));
    return updated;
  };

  const removeReunion = async (id: string) => {
    await deleteReunion(id);

    // Invalidate all reuniones caches for this organization
    if (currentOrg?.id) {
      queryCache.invalidate(`reuniones|org:${currentOrg.id}`);
      queryCache.invalidate(`reuniones-paginated|org:${currentOrg.id}`);
      queryCache.invalidate(`reuniones-calendar|org:${currentOrg.id}`);
    }

    // Refresh to update pagination counts
    await fetchReuniones(page);
  };

  return {
    reuniones,
    loading,
    error,
    page,
    pagination,
    goToPage,
    refresh: () => fetchReuniones(page),
    addReunion,
    modifyReunion,
    removeReunion
  };
}
